import type {
  MetadataSource,
  ModelCapabilities,
  ModelEnrichment,
  ModelOffering,
} from '@bedrock-profiles/shared';

export interface ModelBenchmark {
  name: string;
  score: string;
  rank?: string;
  comparison?: string;
}

export interface ProviderNotes {
  implementationNotes?: string;
  specialFeatures?: string[];
  limitations?: string[];
}

export interface UnifiedModel {
  /** URL-friendly slug (e.g., "claude-sonnet-4.5") */
  slug: string;
  /** Display name (e.g., "Claude Sonnet 4.5") */
  displayName: string;
  /** Model publisher/owner (e.g., "Anthropic") */
  provider: string;
  /** Aggregated capabilities across all deployments */
  capabilities: {
    contextWindow: number;
    maxOutputTokens?: number;
    inputModalities: string[];
    outputModalities: string[];
    streaming: boolean;
  };
  /** All deployments/offerings of this model */
  deployments: ModelDeployment[];
  /** When the first offering was created */
  firstCreated: string;
  /** When the most recent offering was updated */
  lastUpdated: string;
  
  // Rich metadata fields (Phase 1 enhancement)
  /** Full model description */
  description?: string;
  /** One-line summary */
  shortDescription?: string;
  /** URL to official model card/documentation */
  modelCardUrl?: string;
  /** Use case tags (e.g., "Advanced coding", "AI agents") */
  useCases?: string[];
  /** Key strengths/capabilities */
  strengths?: string[];
  /** Best use case summary */
  bestFor?: string;
  /** Release date (ISO format) */
  releaseDate?: string;
  /** Key improvements in this version */
  versionHighlights?: string[];
  /** Benchmark scores and comparisons */
  benchmarks?: ModelBenchmark[];
  /** Provider-specific implementation notes */
  providerNotes?: Record<string, ProviderNotes>;
  /** Whether the rich model copy came from curated metadata or generated fallback metadata */
  metadataSource?: MetadataSource;
  /** Source-backed sections, claims, and freshness details for model-card pages */
  enrichment?: ModelEnrichment;
}

export interface ModelDeployment {
  offeringId: string;
  /** Concrete provider model ID, including dated/revisioned suffixes when present. */
  modelId?: string;
  /** Provider model name when it differs from modelId. */
  modelName?: string;
  cloudProvider: string;
  deploymentType: string;
  sourceRegion?: string | null;
  regions: string[];
  regionalGroup?: string;
  pricingModelName?: string;
  inferenceOptions?: string[];
  pricing?: {
    input: number;
    output: number;
    currency: string;
  };
  residency: 'single-region' | 'geo-boundary' | 'cross-region' | 'global';
  status: 'ACTIVE' | 'INACTIVE' | 'REMOVED';
  capabilities: ModelCapabilities;
}

/**
 * Generate a pretty URL slug from a model key
 * e.g., "claude-sonnet-4-5" → "claude-sonnet-4.5"
 * e.g., "gpt-4o" → "gpt-4o"
 * e.g., "nova-pro-v1" → "nova-pro"
 */
export function generateModelSlug(key: string): string {
  // Convert version separators: 4-5 → 4.5, but only for numeric patterns
  let slug = key.replace(/(\d+)-(\d+)$/, '$1.$2');
  
  // Remove version suffixes like -v1, -v2
  slug = slug.replace(/-v\d+$/, '');
  
  // Remove date patterns
  slug = slug.replace(/-\d{8}(-v\d+)?$/, '');
  slug = slug.replace(/-\d{4}-\d{2}-\d{2}$/, '');
  
  return slug;
}

/**
 * Convert slug back to family key for data lookup
 * e.g., "claude-sonnet-4.5" → "claude-sonnet-4-5"
 */
export function slugToFamilyKey(slug: string): string {
  // Convert 4.5 back to 4-5 for internal lookup
  return slug.replace(/(\d+)\.(\d+)$/, '$1-$2');
}

/**
 * Group offerings into unified models with deployments
 */
export function groupIntoUnifiedModels(offerings: ModelOffering[]): UnifiedModel[] {
  const activeOfferings = offerings.filter((o) => o.status !== 'REMOVED');
  const modelMap = new Map<string, UnifiedModel>();

  for (const offering of activeOfferings) {
    // Generate normalized family key
    const familyKey = normalizeModelId(offering.model.modelId);
    const slug = generateModelSlug(familyKey);
    const mapKey = `${offering.model.provider}::${familyKey}`;

    const existing = modelMap.get(mapKey);
    const destinationRegions = offering.deployment.destinationRegions ?? [];
    
    const deployment: ModelDeployment = {
      offeringId: offering.offeringId,
      modelId: offering.model.modelId,
      modelName: offering.model.modelName,
      cloudProvider: offering.deployment.cloudProvider,
      deploymentType: offering.deployment.deploymentType,
      sourceRegion: offering.deployment.sourceRegion,
      regions: destinationRegions.length > 0
        ? destinationRegions
        : (offering.deployment.sourceRegion ? [offering.deployment.sourceRegion] : []),
      regionalGroup: offering.deployment.regionalGroup,
      pricingModelName: offering.pricingModelName || offering.model.modelId,
      inferenceOptions: offering.inferenceOptions,
      residency: offering.residency?.scope || 'global',
      status: offering.status,
      capabilities: offering.capabilities || {},
    };

    if (existing) {
      existing.deployments.push(deployment);
      
      // Update capabilities to be the max across all deployments
      const caps = offering.capabilities;
      if (caps) {
        if (caps.contextWindowTokens && caps.contextWindowTokens > existing.capabilities.contextWindow) {
          existing.capabilities.contextWindow = caps.contextWindowTokens;
        }
        if (caps.maxOutputTokens && caps.maxOutputTokens > (existing.capabilities.maxOutputTokens || 0)) {
          existing.capabilities.maxOutputTokens = caps.maxOutputTokens;
        }
        if (caps.responseStreamingSupported) {
          existing.capabilities.streaming = true;
        }
        // Merge modalities
        caps.inputModalities?.forEach(m => {
          if (!existing.capabilities.inputModalities.includes(m)) {
            existing.capabilities.inputModalities.push(m);
          }
        });
        caps.outputModalities?.forEach(m => {
          if (!existing.capabilities.outputModalities.includes(m)) {
            existing.capabilities.outputModalities.push(m);
          }
        });
      }
      
      // Update timestamps
      if (offering.createdAt < existing.firstCreated) {
        existing.firstCreated = offering.createdAt;
      }
      if (offering.lastUpdated > existing.lastUpdated) {
        existing.lastUpdated = offering.lastUpdated;
      }
      if (!existing.enrichment && offering.enrichment) {
        existing.enrichment = offering.enrichment;
      }
    } else {
      const caps = offering.capabilities || {};
      modelMap.set(mapKey, {
        slug,
        displayName: keyToDisplayName(familyKey),
        provider: offering.model.provider,
        capabilities: {
          contextWindow: caps.contextWindowTokens || 0,
          maxOutputTokens: caps.maxOutputTokens,
          inputModalities: [...(caps.inputModalities || [])],
          outputModalities: [...(caps.outputModalities || [])],
          streaming: caps.responseStreamingSupported || false,
        },
        deployments: [deployment],
        firstCreated: offering.createdAt,
        lastUpdated: offering.lastUpdated,
        // Rich metadata from first offering (all offerings of same model should have same metadata)
        description: offering.description,
        shortDescription: offering.shortDescription,
        modelCardUrl: offering.modelCardUrl,
        useCases: offering.useCases,
        strengths: offering.strengths,
        bestFor: offering.bestFor,
        releaseDate: offering.releaseDate,
        versionHighlights: offering.versionHighlights,
        benchmarks: offering.benchmarks,
        providerNotes: offering.providerNotes,
        metadataSource: offering.metadataSource,
        enrichment: offering.enrichment,
      });
    }
  }

  return Array.from(modelMap.values()).sort((a, b) => 
    new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime()
  );
}

/**
 * Normalize a model ID into a family key
 * Same logic from modelFamilies.ts
 */
function normalizeModelId(modelId: string): string {
  let id = modelId;
  // Fireworks and similar APIs expose account-scoped model resource names.
  id = id.replace(/^accounts\/[^/]+\/models\//, '');
  // Strip routing/vendor prefixes
  for (let i = 0; i < 2; i++) {
    id = id.replace(/^[a-z]+(?:-[a-z]+)*\.(?=[a-z])/, '');
  }
  // Strip version suffixes
  id = id.replace(/-v\d+$/, '');
  id = id.replace(/-\d{8,}(-v\d+)?$/, '');
  id = id.replace(/-\d{4}-\d{2}-\d{2}$/, '');
  return id;
}

/**
 * Convert key to display name
 * Same logic from modelFamilies.ts
 */
function keyToDisplayName(key: string): string {
  const displayName = key
    .split('/')
    .pop()!
    .split('-')
    .map((part) => {
      if (/^\d+$/.test(part)) return part;
      return part.charAt(0).toUpperCase() + part.slice(1);
    })
    .join(' ')
    .replace(/(\d+) (\d+)/g, '$1.$2');

  return displayName
    .replace(/\bDeepseek\b/g, 'DeepSeek')
    .replace(/\bGpt\b/g, 'GPT')
    .replace(/\bOss\b/g, 'OSS')
    .replace(/\bDbrx\b/g, 'DBRX');
}

/**
 * Get unique cloud providers from deployments
 */
export function getCloudProviders(model: UnifiedModel): string[] {
  const providers = new Set(model.deployments.map(d => d.cloudProvider));
  return Array.from(providers);
}

/**
 * Get minimum pricing across all deployments
 */
export function getMinPricing(model: UnifiedModel): { input: number; output: number } | null {
  let minInput = Infinity;
  let minOutput = Infinity;
  let hasPricing = false;

  for (const deployment of model.deployments) {
    if (deployment.pricing) {
      hasPricing = true;
      if (deployment.pricing.input < minInput) minInput = deployment.pricing.input;
      if (deployment.pricing.output < minOutput) minOutput = deployment.pricing.output;
    }
  }

  return hasPricing ? { input: minInput, output: minOutput } : null;
}

/**
 * Get total unique regions across all deployments
 */
export function getTotalRegionCount(model: UnifiedModel): number {
  const regions = new Set<string>();
  for (const deployment of model.deployments) {
    deployment.regions.forEach(r => regions.add(r));
  }
  return regions.size;
}
